// src/pages/About.js
import React, { useContext, useEffect } from "react";
import { AppContext } from "../App";
import { motion } from "framer-motion";

const About = () => {
  const { language } = useContext(AppContext);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = {
    el: {
      title: "Σχετικά με το VrestoEdo.gr",
      intro: `
Το VrestoEdo.gr δημιουργήθηκε με έναν ξεκάθαρο στόχο: να γίνει το πιο γρήγορο, απλό και αξιόπιστο σημείο αναφοράς για όλους τους πολίτες που αναζητούν ηλεκτρονικά έγγραφα, κρατικές υπηρεσίες και online πληροφορίες μέσα από μια καθαρή και φιλική πλατφόρμα.
      `,
      body1: `
Ζούμε σε μια εποχή όπου μεγάλο μέρος της δημόσιας διοίκησης έχει περάσει στην ψηφιακή εποχή, όμως το πραγματικό πρόβλημα παραμένει: οι πολίτες συχνά δυσκολεύονται να βρουν γρήγορα αυτό που χρειάζονται. Πού βρίσκεται το εκκαθαριστικό; Πώς βρίσκω το ιστορικό ασφάλισής μου; Ποια υπηρεσία είναι η σωστή για ένα συγκεκριμένο πιστοποιητικό; Αυτά τα ερωτήματα ήταν η αφετηρία για τη δημιουργία του VrestoEdo.gr.
      `,
      body2: `
Η πλατφόρμα συγκεντρώνει επιλεγμένους και αξιόπιστους συνδέσμους από επίσημες πηγές, οργανωμένους σε θεματικές κατηγορίες όπως φορολογικά, ασφαλιστικά, δημοτολόγιο, επιδόματα, υγεία, εκπαίδευση και εργασία. Δεν αντικαθιστά τις επίσημες υπηρεσίες, αλλά λειτουργεί ως γρήγορος «χάρτης» που οδηγεί τον χρήστη απευθείας στο σωστό σημείο, χωρίς περιττή αναζήτηση.
      `,
      body3: `
Το VrestoEdo.gr εξελίσσεται διαρκώς, προσθέτοντας νέα άρθρα, επεξηγηματικά κείμενα και κατηγορίες, με στόχο να γίνει ένα πραγματικά χρήσιμο εργαλείο για κάθε πολίτη. Η χρήση του είναι δωρεάν και δεν απαιτεί καμία εγγραφή. Αν έχεις προτάσεις για βελτίωση ή νέες υπηρεσίες που θα ήθελες να προστεθούν, μπορείς πάντα να επικοινωνήσεις μαζί μας.
      `,
      contactTitle: "Επικοινωνία",
      contactText: `
Αν χρειάζεστε βοήθεια, θέλετε να προτείνετε μια νέα υπηρεσία ή εντοπίσατε κάποιον σύνδεσμο που δεν λειτουργεί, μπορείτε να επικοινωνήσετε μαζί μας στο:
      `,
      emailLabel: "Email υποστήριξης:",
      email: "info@vrestoedo.gr",
    },
    en: {
      title: "About VrestoEdo.gr",
      intro: `
VrestoEdo.gr was created with a clear purpose: to become the fastest, simplest and most reliable reference point for citizens seeking online documents, government services and essential digital information through a clean and user–friendly interface.
      `,
      body1: `
Although digital public services in Greece have improved significantly, many people still struggle to locate the exact service or document they need. “Where is my tax statement? How can I access my insurance history? Which service issues a specific certificate?” These are everyday questions that VrestoEdo.gr aims to answer.
      `,
      body2: `
The platform brings together selected, official resources and organizes them into clear categories such as tax, insurance, civil registry, benefits, health, education and employment. We do not replace government portals; instead, we act as a fast navigation layer that guides users directly to the right service without confusion.
      `,
      body3: `
VrestoEdo.gr is continuously evolving, adding new articles, explanatory texts and categories in order to become a truly useful tool for every citizen. It is free to use and does not require any registration. If you have suggestions for improvements or new services that should be included, you are always welcome to contact us.
      `,
      contactTitle: "Contact",
      contactText: `
If you need assistance, want to suggest a new service or have found a broken link, feel free to reach out at:
      `,
      emailLabel: "Support email:",
      email: "info@vrestoedo.gr",
    },
  };

  const t = content[language] || content.el;
  const paragraphs = [t.intro, t.body1, t.body2, t.body3];

  return (
    <motion.div
      className="relative min-h-screen flex flex-col items-center px-4 pt-10 pb-20 text-left"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="absolute inset-0 bg-slate-950/80 -z-10" />

      <div className="max-w-3xl w-full">
        <h1 className="text-3xl font-bold text-white mb-6 drop-shadow-lg">
          {t.title}
        </h1>

        <div className="space-y-4 text-sm sm:text-base text-slate-200 leading-relaxed">
          {paragraphs.map((p, idx) => (
            <p key={idx}>{p.trim()}</p>
          ))}
        </div>

        <div className="mt-10 border-top border-slate-700 pt-6">
          <h2 className="text-xl font-semibold text-white mb-3">
            {t.contactTitle}
          </h2>
          <p className="text-slate-200 mb-2">{t.contactText.trim()}</p>
          <p className="text-slate-100 font-medium">
            {t.emailLabel}{" "}
            <a
              href={`mailto:${t.email}`}
              className="underline text-blue-300 hover:text-blue-200"
            >
              {t.email}
            </a>
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default About;